import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  buttons: {
    color: theme.palette.mode === 'dark' ? '#fff' : '#000',
  },
}));

export const CustomTablePagination = ({
  page,
  rowsPerPage,
  rowsPerPageOptions,
  count,
  onPageChange,
  onRowsPerPageChange,
}: {
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions: readonly number[];
  count: number;
  onPageChange(page: number): void;
  onRowsPerPageChange(itemsPerPage: number): void;
}) => {
  const classes = useStyles();
  const [startNumber, setStartNumber] = useState<number>(0);
  const [endNumber, setEndNumber] = useState<number>(0);
  const [maxPages, setMaxPages] = useState<number>(0);

  const isFirstPageButtonDisabled = (pageNumber: number) => pageNumber === 0;
  const isLastPageButtonDisabled = (
    pageNumber: number,
    maxNumberPages: number,
  ) => {
    return pageNumber === maxNumberPages;
  };

  useEffect(() => {
    setStartNumber(page * rowsPerPage + 1);
    const endNumberValue = page * rowsPerPage + rowsPerPage;
    setEndNumber(count < endNumberValue ? count : endNumberValue);
    setMaxPages(Math.ceil(count / rowsPerPage) - 1);
  }, [page, rowsPerPage, count]);

  const formatResults = (
    startValue: number,
    endValue: number,
    countValue: number,
  ): string => {
    return endValue - startValue !== 0
      ? `${startValue} - ${endValue} of ${countValue}`
      : `${endValue} of ${countValue}`;
  };

  return (
    count > 0 && (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          data-testid="entity-table-pagination"
          sx={{ alignItems: 'center', display: 'flex' }}
        >
          <Typography sx={{ mr: 1 }} variant="body2">
            Rows per page:
          </Typography>
          <Select
            data-testid="table-pagination-limit-per-page-dropdown"
            onChange={event => onRowsPerPageChange(+event.target.value)}
            size="small"
            variant="standard"
            sx={{
              boxShadow: 'none',
              fontSize: '14px',
              '&.v5-MuiInputBase-root': {
                borderBottom: 'none',
              },
              '&.v5-MuiInput-underline:before': {
                borderBottom: 'none',
              },
              '&.v5-MuiInput-underline:after': {
                borderBottom: 'none',
              },
              '& .v5-MuiSelect-select': {
                padding: 0,
              },
            }}
            value={rowsPerPage}
          >
            {rowsPerPageOptions.map(val => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ alignItems: 'center', display: 'flex' }}>
          <Typography
            data-testid="table-pagination-results"
            sx={{ mr: 1 }}
            variant="body2"
          >
            {formatResults(startNumber, endNumber, count)}
          </Typography>
          <IconButton
            data-testid="table-pagination-first-page-button"
            disabled={isFirstPageButtonDisabled(page)}
            onClick={() => onPageChange(0)}
            size="small"
          >
            <FirstPageIcon
              className={
                !isFirstPageButtonDisabled(page) ? classes.buttons : ''
              }
            />
          </IconButton>
          <IconButton
            data-testid="table-pagination-back-button"
            disabled={isFirstPageButtonDisabled(page)}
            onClick={() => onPageChange(page - 1)}
            size="small"
          >
            <NavigateBeforeIcon
              className={
                !isFirstPageButtonDisabled(page) ? classes.buttons : ''
              }
            />
          </IconButton>
          <IconButton
            data-testid="table-pagination-next-button"
            disabled={isLastPageButtonDisabled(page, maxPages)}
            onClick={() => onPageChange(page + 1)}
            size="small"
          >
            <NavigateNextIcon
              className={
                !isLastPageButtonDisabled(page, maxPages) ? classes.buttons : ''
              }
            />
          </IconButton>
          <IconButton
            data-testid="table-pagination-last-page-button"
            disabled={isLastPageButtonDisabled(page, maxPages)}
            onClick={() => onPageChange(maxPages)}
            size="small"
          >
            <LastPageIcon
              className={
                !isLastPageButtonDisabled(page, maxPages) ? classes.buttons : ''
              }
            />
          </IconButton>
        </Box>
      </Box>
    )
  );
};
